/**
 * HOMEPAGE SLIDERS V2 — BEGIN, 2026-09-15.
 * Новый независимый механизм для услуг, курсов и отзывов.
 * Старые классы/обработчики здесь не используются и при v2 не загружаются.
 * Откат: 'v2' → 'legacy' в snippets/homepage-slider-version.liquid, reload.
 *
 * Единственный владелец анимации — requestAnimationFrame. CSS snap/smooth
 * отключены только у подключённых треков. Свайп/тачпад прокручивает нативно,
 * после завершения жеста этот же движок доводит трек до ближайшей позиции.
 * Стрелки считают шаги от целевой позиции, даже пока предыдущий шаг движется.
 */
(() => {
  'use strict';

  // Секции могут подключать asset несколько раз, в том числе в theme editor.
  if (window.SkillsHomepageSlidersV2) return;

  const MOTION = Object.freeze({
    duration: 400, // Новая явная длительность: старый smooth зависел от браузера.
    ease: (t) => 1 - Math.pow(1 - t, 5), // Плавное затухание, как у Lenis в теме.
    tolerance: 2, // Сохранённый допуск для дробных координат.
    settleDelay: 200, // Прежняя задержка объявления отзыва, также конец жеста.
    wheelGap: 300,
    wheelThreshold: 40,
    wheelCooldown: 400,
    wheelLine: 16, // Параметры чувствительности колеса из прежних услуг.
    dragThreshold: 8,
  });
  const KINDS = [
    { root: 'courses-carousel', prefix: 'courses', slides: '.courses-grid__item' },
    { root: 'services-slider', prefix: 'services', slides: '.services__item', verticalWheel: true },
    { root: '.testimonials', prefix: 'testimonials', slides: '[data-testimonials-slide]' },
  ];
  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  class HomepageSlider {
    constructor(root, kind) {
      this.root = root;
      this.kind = kind;
      const find = (suffix) => root.querySelector(`[data-${kind.prefix}-${suffix}]`);
      this.viewport = find('track');
      this.nav = find('nav');
      this.prev = find('prev');
      this.next = find('next');
      this.status = find('status');
      this.ready = !!(this.viewport && this.nav && this.prev && this.next);
      if (!this.ready) return;

      this.originalTabindex = this.viewport.getAttribute('tabindex');
      this.originalNavHidden = this.nav.hidden;
      this.originalDisabled = [this.prev.disabled, this.next.disabled];
      this.abort = new AbortController();
      this.stops = [0];
      this.targetIndex = null;
      this.frame = 0;
      this.pointer = null;
      this.wheel = { time: -Infinity, amount: 0, cooldown: 0 };
      this.viewport.setAttribute('data-homepage-slider-v2', '');

      const on = (element, type, handler, options = {}) => {
        element.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      };
      on(this.prev, 'click', () => this.step(-1));
      on(this.next, 'click', () => this.step(1));
      on(this.viewport, 'scroll', () => this.onScroll(), { passive: true });
      on(this.viewport, 'wheel', (event) => this.onWheel(event), { passive: false });
      on(this.viewport, 'keydown', (event) => this.onKey(event));
      on(this.viewport, 'pointerdown', (event) => this.onPointerDown(event));
      on(window, 'pointermove', (event) => this.onPointerMove(event), { passive: false });
      on(window, 'pointerup', (event) => this.onPointerEnd(event));
      on(window, 'pointercancel', (event) => this.onPointerEnd(event));
      on(window, 'blur', () => this.endPointer());
      on(this.viewport, 'dragstart', (event) => event.preventDefault());
      on(this.viewport, 'click', (event) => {
        if (!this.suppressClick || event.detail === 0) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { capture: true });
      on(reducedMotion, 'change', () => {
        if (reducedMotion.matches && this.targetIndex !== null) this.goTo(this.targetIndex, false);
      });

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.mutationObserver = new MutationObserver(() => this.observeSlides());
      this.mutationObserver.observe(this.viewport, { childList: true, subtree: true });
      this.observeSlides();
      this.nav.hidden = false;
    }

    // Координаты всегда измеряются из реальной раскладки; ширина/зазор не угаданы.
    observeSlides() {
      this.slides = Array.from(this.root.querySelectorAll(this.kind.slides));
      this.resizeObserver.disconnect();
      this.resizeObserver.observe(this.viewport);
      this.slides.forEach((slide) => this.resizeObserver.observe(slide));
      this.measure();
    }

    read() {
      return clamp(this.viewport.scrollLeft * (this.rtl ? -1 : 1), 0, this.max || 0);
    }

    write(position) {
      this.viewport.scrollLeft = position * (this.rtl ? -1 : 1);
    }

    nearest(position) {
      return this.stops.reduce((best, stop, index) =>
        Math.abs(stop - position) < Math.abs(this.stops[best] - position) ? index : best, 0);
    }

    measure() {
      const oldStops = this.stops;
      const requested = this.targetIndex;
      this.rtl = getComputedStyle(this.viewport).direction === 'rtl';
      this.max = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const position = this.read();
      const bounds = this.viewport.getBoundingClientRect();
      const leading = this.rtl
        ? bounds.left + this.viewport.clientLeft + this.viewport.clientWidth
        : bounds.left + this.viewport.clientLeft;
      this.offsets = this.slides.map((slide) => {
        const rect = slide.getBoundingClientRect();
        return position + (this.rtl ? leading - rect.right : rect.left - leading);
      });
      this.stops = [0];
      this.offsets.forEach((offset) => {
        const stop = clamp(offset, 0, this.max);
        if (stop - this.stops[this.stops.length - 1] > MOTION.tolerance) this.stops.push(stop);
      });
      // Отдельный конечный упор нужен, когда последняя карточка видна лишь частично.
      if (this.max - this.stops[this.stops.length - 1] > MOTION.tolerance) this.stops.push(this.max);
      const changed = oldStops.length !== this.stops.length ||
        this.stops.some((stop, index) => Math.abs(stop - oldStops[index]) > MOTION.tolerance);
      if (changed && requested !== null) this.goTo(clamp(requested, 0, this.stops.length - 1), false);
      else if (changed && !this.pointer) this.goTo(this.nearest(position), false);
      this.update();
    }

    update() {
      const position = this.targetIndex === null ? this.read() : this.stops[this.targetIndex];
      this.prev.disabled = position <= MOTION.tolerance;
      this.next.disabled = position >= this.max - MOTION.tolerance;
      if (this.max > MOTION.tolerance) this.viewport.setAttribute('tabindex', '0');
      else this.viewport.removeAttribute('tabindex');
    }

    announce() {
      if (!this.status?.dataset.template || !this.slides.length) return;
      const position = this.read();
      // У отзывов бывают разные ширины: объявляем фактическую ведущую карточку.
      const index = this.offsets.reduce((best, offset, i) =>
        Math.abs(offset - position) < Math.abs(this.offsets[best] - position) ? i : best, 0);
      this.status.textContent = this.status.dataset.template
        .replace('[current]', index + 1).replace('[total]', this.slides.length);
    }

    stop() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.targetIndex = null;
      clearTimeout(this.settleTimer);
    }

    goTo(index, animate = true) {
      this.stop();
      index = clamp(index, 0, this.stops.length - 1);
      const from = this.read();
      const to = this.stops[index];
      const finish = () => {
        this.write(to);
        this.frame = 0;
        this.targetIndex = null;
        this.update();
        this.announce();
      };
      if (!animate || reducedMotion.matches || Math.abs(to - from) <= MOTION.tolerance) {
        finish();
        return;
      }
      this.targetIndex = index;
      this.update();
      const started = performance.now();
      const tick = (now) => {
        const progress = clamp((now - started) / MOTION.duration, 0, 1);
        this.write(from + (to - from) * MOTION.ease(progress));
        if (progress < 1) this.frame = requestAnimationFrame(tick);
        else finish();
      };
      this.frame = requestAnimationFrame(tick);
    }

    step(direction) {
      const base = this.targetIndex ?? this.nearest(this.read());
      const target = clamp(base + direction, 0, this.stops.length - 1);
      if (target !== base || this.targetIndex === null) this.goTo(target);
    }

    onScroll() {
      this.update();
      if (this.targetIndex === null) this.scheduleSettle();
    }

    scheduleSettle() {
      clearTimeout(this.settleTimer);
      if (this.pointer) return;
      this.settleTimer = setTimeout(() => {
        if (this.targetIndex === null) this.goTo(this.nearest(this.read()));
      }, MOTION.settleDelay);
    }

    onKey(event) {
      if (event.target !== this.viewport || event.altKey || event.ctrlKey || event.metaKey) return;
      const directions = { ArrowLeft: this.rtl ? 1 : -1, ArrowRight: this.rtl ? -1 : 1 };
      if (event.key in directions) {
        event.preventDefault();
        this.step(directions[event.key]);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.goTo(event.key === 'Home' ? 0 : this.stops.length - 1);
      }
    }

    onWheel(event) {
      if (event.ctrlKey || event.metaKey || this.max <= MOTION.tolerance) return;
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
      if (horizontal) {
        this.stop();
        // Горизонтальное нативное движение не должно перехватываться Lenis страницы.
        event.stopPropagation();
        this.scheduleSettle();
        return;
      }
      if (!this.kind.verticalWheel || !event.deltaY) return;
      const now = performance.now();
      const delta = event.deltaY * (event.deltaMode === 1 ? MOTION.wheelLine :
        event.deltaMode === 2 ? this.viewport.clientWidth : 1);
      const direction = Math.sign(delta);
      if (now - this.wheel.time > MOTION.wheelGap || Math.sign(this.wheel.amount) !== direction) {
        this.wheel.amount = 0;
      }
      this.wheel.time = now;
      const base = this.targetIndex ?? this.nearest(this.read());
      const canMove = direction > 0 ? base < this.stops.length - 1 : base > 0;
      if (!canMove && now >= this.wheel.cooldown) return; // На краю колесо листает страницу.
      event.preventDefault();
      event.stopPropagation();
      if (now < this.wheel.cooldown) return;
      this.wheel.amount += delta;
      if (Math.abs(this.wheel.amount) < MOTION.wheelThreshold) return;
      this.wheel.amount = 0;
      this.wheel.cooldown = now + MOTION.wheelCooldown;
      this.step(direction);
    }

    onPointerDown(event) {
      if (!event.isPrimary || event.button !== 0) return;
      this.stop();
      this.suppressClick = false;
      this.pointer = {
        id: event.pointerId, type: event.pointerType,
        x: event.clientX, y: event.clientY, position: this.read(), dragging: false,
        interactive: !!event.target.closest('input, textarea, select, button, [contenteditable="true"]'),
      };
    }

    onPointerMove(event) {
      const pointer = this.pointer;
      // Touch/pen используют браузерную прокрутку с инерцией и вертикальный scroll страницы.
      if (!pointer || pointer.id !== event.pointerId || pointer.type !== 'mouse' || pointer.interactive) return;
      const dx = event.clientX - pointer.x;
      if (!pointer.dragging) {
        if (Math.abs(dx) < MOTION.dragThreshold || Math.abs(dx) < Math.abs(event.clientY - pointer.y)) return;
        if (this.max <= MOTION.tolerance) return;
        pointer.dragging = true;
        this.viewport.setPointerCapture(event.pointerId);
        this.viewport.setAttribute('data-dragging', '');
      }
      event.preventDefault();
      this.write(clamp(pointer.position - dx * (this.rtl ? -1 : 1), 0, this.max));
    }

    onPointerEnd(event) {
      if (this.pointer?.id === event.pointerId) this.endPointer();
    }

    endPointer() {
      if (!this.pointer) return;
      const { id, dragging } = this.pointer;
      this.suppressClick = dragging;
      this.pointer = null;
      this.viewport.removeAttribute('data-dragging');
      if (this.viewport.hasPointerCapture(id)) this.viewport.releasePointerCapture(id);
      this.scheduleSettle();
    }

    reveal(block) {
      const index = this.slides.findIndex((slide) => slide === block || slide.contains(block));
      if (index >= 0) this.goTo(this.nearest(clamp(this.offsets[index], 0, this.max)), false);
    }

    destroy() {
      this.endPointer();
      this.stop();
      this.abort.abort();
      this.resizeObserver.disconnect();
      this.mutationObserver.disconnect();
      this.viewport.removeAttribute('data-homepage-slider-v2');
      if (this.originalTabindex === null) this.viewport.removeAttribute('tabindex');
      else this.viewport.setAttribute('tabindex', this.originalTabindex);
      this.nav.hidden = this.originalNavHidden;
      [this.prev.disabled, this.next.disabled] = this.originalDisabled;
    }
  }

  function init(scope = document) {
    KINDS.forEach((kind) => {
      const roots = [...(scope.matches?.(kind.root) ? [scope] : []), ...scope.querySelectorAll(kind.root)];
      roots.forEach((root) => {
        if (instances.has(root)) return;
        const instance = new HomepageSlider(root, kind);
        if (instance.ready) instances.set(root, instance);
      });
    });
  }

  function unload(scope) {
    instances.forEach((instance, root) => {
      if (root === scope || scope.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  window.SkillsHomepageSlidersV2 = Object.freeze({ init, unload });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
  document.addEventListener('shopify:section:reorder', () => instances.forEach((instance) => instance.measure()));
  document.addEventListener('shopify:block:select', (event) => {
    instances.forEach((instance, root) => {
      if (root.contains(event.target)) instance.reveal(event.target);
    });
  });
})();
// HOMEPAGE SLIDERS V2 — END.
